import { cn, IconButton, TabItem, Tabs, Text, useBoolean } from "@aurem/ui";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ApiTab } from "./tabs/ApiTab";
import { DataTab } from "./tabs/DataTab";
import { LinksTab } from "./tabs/LinksTab";
import { TemporalTab } from "./tabs/TemporalTab";

const isLocal = process.env.REACT_APP_ENVIRONMENT === "local";
const isDevToolbarEnabled = process.env.DEV_TOOLBAR_ENABLED === "true";

type TabId = "api" | "temporal" | "quick-access" | "data";

const TABS: TabItem[] = [
  { id: "temporal", text: "Temporal" },
  { id: "api", text: "API" },
  ...(isLocal ? [{ id: "quick-access", text: "Quick access" }] : []),
  { id: "data", text: "Data" },
];

export const DevToolbar = () => {
  const { value: isOpen, toggle, setFalse: close } = useBoolean(false);
  const [activeTab, setActiveTab] = useState<string>(TABS[0].id);

  useEffect(() => {
    if (!isDevToolbarEnabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "'") {
        event.preventDefault();
        event.stopPropagation();
        toggle();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggle]);

  const shortcutKey = useMemo(() => {
    const isMac =
      typeof navigator !== "undefined" && /Mac/.test(navigator.userAgent);
    return isMac ? "⌘'" : "Ctrl+'";
  }, []);

  if (!isDevToolbarEnabled) {
    return null;
  }

  return (
    <div
      className={cn(
        "fixed bottom-0 start-0 end-0 z-[100]",
        "transform transition-all duration-300 ease-in-out",
        isOpen ? "translate-y-0 visible" : "translate-y-full invisible",
      )}
      data-e2e-id="devtoolbar"
    >
      <div className="bg-white border-t border-gray200 shadow-lg h-[280px] flex flex-col">
        <div className="flex items-center justify-between px-4 h-10 border-b border-gray100 shrink-0">
          <Tabs
            items={TABS}
            activeItem={activeTab}
            onChange={(id) => setActiveTab(id)}
            data-e2e-id="devtoolbar-tabs"
          />

          <div className="flex items-center gap-3">
            <Text size="xs" className="text-gray400">
              {shortcutKey} to toggle
            </Text>
            <Text size="xs" className="text-gray500 font-medium">
              Dev toolbar
            </Text>
            <IconButton
              variant="ghost"
              color="neutral"
              size="sm"
              onClick={close}
              aria-label="Close dev toolbar"
              data-e2e-id="devtoolbar-close"
            >
              <X />
            </IconButton>
          </div>
        </div>

        <div data-e2e-id="devtoolbar-content">
          {activeTab === "api" && <ApiTab />}
          {activeTab === "temporal" && <TemporalTab />}
          {activeTab === "quick-access" && <LinksTab />}
          {activeTab === "data" && <DataTab />}
        </div>
      </div>
    </div>
  );
};
